import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Link,
  TextField,
  Typography,
  Stack,
  useTheme,
} from "@mui/material";
import {
  useLocation,
  useNavigate,
  Link as RouterLink,
  useSearchParams,
} from "react-router-dom";
import { useAuth } from "../../features/accounts/useAuth";
import { apiUrl } from "../../services/apiClient";
import { useRecaptcha } from "../../components/forms/Recaptcha";
import { useCallback, useState, useMemo, useEffect } from "react";
import { dataPatterns } from "../../features/data/data-types";
import CookieConsentDialog from "../../components/dialogs/CookieConsentDialog";
import { ProcessButton } from "../../components/buttons/ProcessButton";
import { AttributionService } from "../../services/attribution.service";
import { MAIN_SITE_URL } from "../../services/site.config";
import {
  setLoginPreference,
  joinAccount,
  consumePendingJoin,
} from "../../features/accounts/auth.service";

// Helper function to set login preference cookie

// Helper function to get V1 login URL
const getV1LoginUrl = (email?: string, returnUrl?: string) => {
  const baseUrl = MAIN_SITE_URL + "/account/signin";
  const params = new URLSearchParams();

  if (email) params.set("email", email);
  if (returnUrl) params.set("returnUrl", returnUrl);
  params.set("fromV2", "true");

  return params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
};

type LoginData = {
  email?: string;
  password?: string;
};

type LocationState = {
  from?: {
    pathname: string;
  };
  // Set by the /join page's "Sign in" fallback for an invitee who already has
  // an account — completes the workspace join once authenticated.
  joinCode?: string;
  joinEmail?: string;
};

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const [searchParams] = useSearchParams();

  const from = (location.state as LocationState)?.from?.pathname || "/";
  const { doLogin } = useAuth();

  const externalUrl = useMemo(() => {
    // Carry an existing-account invite through the SSO round-trip so the join
    // completes after Google auth (mirrors the email+password path).
    const joinState = location.state as LocationState;
    const joinParams =
      joinState?.joinCode && joinState?.joinEmail
        ? `&ic=${encodeURIComponent(
            joinState.joinCode
          )}&je=${encodeURIComponent(joinState.joinEmail)}`
        : "";

    const baseUrl =
      apiUrl +
      `/auth/external-login?returnUrl=` +
      encodeURIComponent(window.location.origin + "/external-login") +
      joinParams;

    // Add attribution parameters to external login URL
    const attributionParams = AttributionService.getForExternalAuth();
    return attributionParams ? `${baseUrl}&${attributionParams}` : baseUrl;
  }, [location.state]);

  // Store attribution parameters when component loads
  useEffect(() => {
    if (searchParams.size > 0) {
      AttributionService.storeFromCurrentUrl(searchParams);
    }
  }, [searchParams]);

  const { token, isLoadingToken, regenerateToken } = useRecaptcha("login");

  const [loginData, setLoginData] = useState<LoginData>({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState<LoginData>({});
  const [signInFailed, setSignInFailed] = useState(false);

  const [isProcessing, setIsProcessing] = useState(false);

  // Pre-fill the email when arriving from a workspace invite ("Sign in" on
  // /join) so the session email matches the invited email and the join can
  // complete after authentication.
  useEffect(() => {
    const joinEmail = (location.state as LocationState)?.joinEmail;
    if (joinEmail) {
      setLoginData((prev) => ({ ...prev, email: joinEmail }));
    }
  }, [location.state]);

  const validateForm = () => {
    let valid = true;
    const newErrors = { email: "", password: "" };

    if (!loginData.email || !dataPatterns.email.test(loginData.email)) {
      newErrors.email = "Please enter a valid email";
      valid = false;
    }

    if (!loginData.password) {
      newErrors.password = "Please enter a valid password";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = async () => {
    //event.preventDefault();
    if (!validateForm()) return;

    setIsProcessing(true);

    let currentToken = token;
    if (isLoadingToken || !currentToken) {
      currentToken = await regenerateToken();
      if (!currentToken) {
        setErrors({
          ...errors,
          email: "Unable to verify reCAPTCHA. Please try again.",
        });
        setIsProcessing(false);
        return;
      }
    }

    try {
      // Default the V2 app to a persistent (14-day) session — no "Remember me" checkbox.
      // CM is used periodically, so a 24h session logs users out between visits; staying
      // signed in is the better default for an app (active sessions still refresh via
      // auth/extend-session). logout() covers the shared-device case.
      const response = await doLogin({ ...loginData, token: currentToken, rememberMe: true });
      if (response.succeeded) {
        setLoginPreference();
        // Existing-account invitee: complete the workspace join now that we're
        // authenticated. Prefer the sessionStorage invite (set by /join, robust
        // across refresh), falling back to router state from the "Sign in" link.
        const joinState = location.state as LocationState;
        const pending = consumePendingJoin();
        const joinCode = pending?.code ?? joinState?.joinCode;
        const joinEmail = pending?.email ?? joinState?.joinEmail;
        if (joinCode && joinEmail) {
          // /auth/join links the AccountUser and switches into the invited
          // workspace. If it fails (e.g. email mismatch) we still proceed —
          // they are logged in.
          try {
            await joinAccount(joinEmail, joinCode);
          } catch {
            /* logged in regardless */
          }
          // Hard reload (not navigate) so the session re-bootstraps with the
          // refreshed account list + switched account — a SPA navigate would
          // keep the stale in-memory session and hide the new workspace.
          window.location.href = "/";
          return;
        }
        navigate(from, { replace: true });
      } else {
        setErrors({
          ...errors,
          email: response?.message || "Login failed. Please try again.",
        });
        // Only the server-rejected branch — not client validation, not the reCAPTCHA
        // path, not the network catch below. Those are different problems and the
        // provider hint would be noise on them.
        setSignInFailed(true);
        await regenerateToken();
        setIsProcessing(false);
      }
    } catch {
      setErrors({
        ...errors,
        email: "An error occurred. Please try again.",
      });
      await regenerateToken();
      setIsProcessing(false);
    }
  };

  const handleInputChange = useCallback(
    (event) => {
      const { name, value } = event.target;
      setLoginData({ ...loginData, [name]: value });
    },
    [loginData]
  );

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleSubmit();
    }
  };

  return (
    <Container sx={{ pt: 5, pb: 5 }} maxWidth="sm">
      <Box mb={5} display="flex" justifyContent="center">
        <RouterLink to="/">
          <img alt="Logo" src="/images/logo.png" />
        </RouterLink>
      </Box>

      <Card sx={{ maxHeight: "90vh", overflowY: "auto" }}>
        <CardContent sx={{ padding: 3 }}>
          <Box mb={2}>
            <Typography color="textPrimary" gutterBottom variant="h4">
              Sign in
            </Typography>
          </Box>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{ width: "100%" }}
          >
            <Button
              size="large"
              variant="outlined"
              href={externalUrl + "&provider=Google"}
              fullWidth
              sx={{
                justifyContent: "flex-start",
                py: 1,
                flex: { xs: "1", sm: "3" },
                whiteSpace: "nowrap",
              }}
            >
              <img
                alt="Google"
                style={{
                  marginRight: "16px",
                  width: 24,
                  height: 24,
                }}
                src="/images/google.svg"
              />
              Sign in with Google
            </Button>
          </Stack>

          <Box mt={3} mb={2}>
            <Typography color="textPrimary">
              or use your email to sign in:
            </Typography>
          </Box>

          <Box>
            <form>
              <TextField
                error={Boolean(errors.email)}
                fullWidth
                helperText={errors.email}
                label="Email"
                name="email"
                onBlur={validateForm}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                type="email"
                value={loginData.email}
                variant="outlined"
                slotProps={{
                  inputLabel: { shrink: true },
                }}
                placeholder="Enter your email"
                autoComplete="email"
                sx={{ mb: 2 }}
              />

              <TextField
                error={Boolean(errors.password)}
                fullWidth
                helperText={errors.password}
                label="Password"
                name="password"
                onBlur={validateForm}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                type="password"
                value={loginData.password}
                variant="outlined"
                placeholder="Enter your password"
                slotProps={{
                  inputLabel: { shrink: true },
                }}
                sx={{ mb: 2 }}
              />

              <Box>
                <ProcessButton
                  onClick={handleSubmit}
                  size="large"
                  disabled={isProcessing || isLoadingToken}
                  processing={isProcessing}
                  label={"Log In"}
                  color={"primary"}
                  variant="contained"
                  fullWidth
                />
              </Box>
            </form>
          </Box>

          {/* Shown only after a rejected sign-in. A provider-only account has no password hash, so
              CheckPasswordAsync can never succeed and the generic error reads as "wrong password" —
              which sends people to reset a password that was never the problem. Static and
              account-independent on purpose: naming the provider for a given email would tell an
              unauthenticated caller which provider that email uses, a phishing aid. The server logs
              the real hits (AppEvents.SignInMethodMismatch). */}
          {signInFailed && (
            <Box mt={2}>
              <Typography variant="body2" color="textSecondary">
                Signed up with Google? Use the button above.
              </Typography>
            </Box>
          )}

          <Box mt={2} mb={1}>
            <Link
              component={RouterLink}
              to="/forgot-password"
              variant="body2"
              color="textSecondary"
            >
              Forgot password?
            </Link>
          </Box>

          {/* Ensure the sign-up section is always visible */}
          <Box mt={2} mb={1}>
            <Divider />
          </Box>

          <Box
            py={1}
            sx={{
              backgroundColor: theme.palette.background.paper,
              textAlign: "center",
            }}
          >
            <Typography variant="body1" color="textSecondary">
              Don&apos;t have an account?{" "}
              <Link
                component={RouterLink}
                to={"/signup" + (from ? "?from=" + from : "")}
                variant="body1"
                color="secondary"
                sx={{ fontWeight: "medium" }}
              >
                Sign up
              </Link>
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Box
        sx={{
          textAlign: "center",
          mt: 2,
          p: 2,
          border: "1px solid #e0e0e0",
          borderRadius: 1,
          backgroundColor: "#f9f9f9",
        }}
      >
        <Button
          variant="outlined"
          size="small"
          href={getV1LoginUrl(
            loginData.email,
            searchParams.get("returnUrl") || undefined
          )}
          sx={{
            textTransform: "none",
            borderColor: "#ccc",
            color: "#666",
            "&:hover": {
              borderColor: "#999",
              backgroundColor: "#f0f0f0",
            },
          }}
        >
          Sign in with Classic App (V1)
        </Button>
      </Box>

      <Typography
        component="div"
        sx={{
          textAlign: "center",
          mt: 2,
          color: "#999",
          fontSize: 10,
          a: { color: "#999" },
        }}
      >
        This site is protected by reCAPTCHA and the Google{" "}
        <a href="https://policies.google.com/privacy">Privacy Policy</a> and{" "}
        <a href="https://policies.google.com/terms">Terms of Service</a> apply.
      </Typography>
      <CookieConsentDialog />
    </Container>
  );
};

export default Login;
